import React, { useState, useMemo } from "react";
import {
  PDFDownloadLink,
  Text,
  View,
  StyleSheet,
  PDFViewer,
  Document,
  Page,
  Image,
} from "@react-pdf/renderer";

// PDF Layout Constants - Same as client statement
const PAGE_HEIGHT = 792; // US Letter size in points
const PAGE_WIDTH = 612; // US Letter width in points
const MARGIN = 40; // Standard margins
const HEADER_HEIGHT = 80; // Fixed header height
const FOOTER_HEIGHT = 40; // Fixed footer height  
const TABLE_HEADER_HEIGHT = 20; // Table header row height
const ROW_HEIGHT = 18; // Fixed row height for consistency - increased to ensure text visibility
const SUMMARY_HEIGHT = 60; // Fixed summary section height

// Font settings for consistency - Using monospace for predictable text calculations
const FONT_FAMILY = 'Courier';
const FONT_SIZE_LARGE = 14; // Headers and titles
const FONT_SIZE_NORMAL = 10; // Body text
const FONT_SIZE_SMALL = 8; // Footer and fine print

// Calculate available content space
const CONTENT_HEIGHT = PAGE_HEIGHT - 2 * MARGIN - HEADER_HEIGHT - FOOTER_HEIGHT;
const FIRST_PAGE_CLIENT_INFO_HEIGHT = 40; // Space for client info on first page
const FIRST_PAGE_CONTENT_HEIGHT = CONTENT_HEIGHT - FIRST_PAGE_CLIENT_INFO_HEIGHT;

// Calculate exact rows per page (adjusted based on manual height calculations)
const ROWS_PER_FIRST_PAGE = 28; // Will be dynamically calculated based on content height
const ROWS_PER_CONTINUATION_PAGE = 34; // Will be dynamically calculated based on content height

const styles = StyleSheet.create({
  // Page layout
  page: {
    fontFamily: FONT_FAMILY,
    padding: MARGIN,
    fontSize: FONT_SIZE_NORMAL,
  },
  
  // Header styles
  header: {
    height: HEADER_HEIGHT,
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "flex-start",
    marginBottom: 10,
    borderBottomWidth: 1,
    borderBottomColor: "#CCCCCC",
    paddingBottom: 10,
  },
  logo: {
    width: 80,
    height: 40,
  },
  clinicInfo: {
    alignItems: "flex-end",
    flex: 1,
  },
  clinicName: {
    fontSize: FONT_SIZE_LARGE,
    fontWeight: "bold",
    color: "#0066CC",
    marginBottom: 2,
  },
  clinicDetails: {
    fontSize: FONT_SIZE_SMALL,
    color: "#333333",
    marginBottom: 1,
    textAlign: "right",
  },
  title: {
    fontSize: FONT_SIZE_LARGE + 2,
    fontWeight: "bold",
    color: "#0066CC",
    textAlign: "center",
    marginTop: 10,
  },
  
  // Footer styles
  footer: {
    position: "absolute",
    bottom: MARGIN,
    left: MARGIN,
    right: MARGIN,
    height: FOOTER_HEIGHT,
    borderTopWidth: 1,
    borderTopColor: "#CCCCCC",
    paddingTop: 8,
    justifyContent: "center",
    alignItems: "center",
  },
  footerText: {
    fontSize: FONT_SIZE_SMALL,
    color: "#666666",
    textAlign: "center",
    marginBottom: 2,
  },
  
  // Content styles
  clientInfoSection: {
    height: FIRST_PAGE_CLIENT_INFO_HEIGHT,
    marginBottom: 10,
  },
  testInfo: {
    fontSize: FONT_SIZE_NORMAL,
    textAlign: "center",
    marginBottom: 8,
    fontWeight: "bold",
    backgroundColor: "#FFF3CD",
    padding: 5,
    borderWidth: 1,
    borderColor: "#FFEAA7",
  },
  
  // Table styles
  tableHeader: {
    height: TABLE_HEADER_HEIGHT,
    flexDirection: "row",
    backgroundColor: "#F0F0F0",
    borderTopWidth: 1,
    borderBottomWidth: 1,
    borderColor: "#000000",
    alignItems: "center",
    paddingHorizontal: 4,
  },
  tableHeaderText: {
    fontWeight: "bold",
    fontSize: FONT_SIZE_NORMAL,
    color: "#000000",
  },
  
  // Table row styles - removed paddingVertical to fix spacing math
  row: {
    height: ROW_HEIGHT,
    flexDirection: "row",
    alignItems: "flex-start", // Top-align all columns when text wraps
    paddingHorizontal: 4,
    // Removed paddingVertical to fix uneven top/bottom spacing that throws off calculations
    borderBottomWidth: 0.5,
    borderBottomColor: "#EEEEEE",
    justifyContent: "flex-start",
  },
  
  // Column styles with exact widths and tighter text alignment
  numberCol: { 
    width: "10%", 
    fontSize: FONT_SIZE_NORMAL, 
    textAlign: "center",
    lineHeight: 1.1, // Tighter line height to reduce gaps
    paddingTop: 2 // Reduced padding
  },
  contentCol: { 
    width: "70%", 
    fontSize: FONT_SIZE_NORMAL,
    lineHeight: 1.1, // Tighter line height to reduce gaps
    paddingTop: 2 // Reduced padding
  },
  pageCol: { 
    width: "20%", 
    fontSize: FONT_SIZE_NORMAL, 
    textAlign: "center",
    lineHeight: 1.1, // Tighter line height to reduce gaps
    paddingTop: 2 // Reduced padding
  },
  
  // Summary styles
  summarySection: {
    height: SUMMARY_HEIGHT,
    borderTopWidth: 2,
    borderTopColor: "#000000",
    paddingTop: 8,
    justifyContent: "center",
    alignItems: "center",
    backgroundColor: "#E8F5E8",
  },
  summaryText: {
    fontSize: FONT_SIZE_NORMAL,
    fontWeight: "bold",
    textAlign: "center",
    marginBottom: 5,
  },
});

// Calculate dynamic row height based on text content
const calculateRowHeight = (content, columnWidth = 240) => {
  // With Courier monospace font at 10pt, calculate precise height
  // More conservative calculation to prevent extra blank space
  const charsPerLine = 73; // EXACT visual measurement - 73 'a' characters fit per line
  const lines = Math.max(1, Math.ceil(content.length / charsPerLine));
  
  // Different height calculation for single vs multi-line
  if (lines === 1) {
    return 18; // Standard single-line height
  } else {
    // For multi-line: base height + additional height per extra line
    const baseHeight = 18; // Height for first line
    const additionalLineHeight = 12; // Height for each additional line (tighter)
    const calculatedHeight = baseHeight + ((lines - 1) * additionalLineHeight);
    
    console.log(`[Row Height] Text: "${content.substring(0, 40)}..." | Chars: ${content.length} | Lines: ${lines} | Height: ${calculatedHeight}pt`);
    return calculatedHeight;
  }
};

// Advanced pagination function that accounts for text wrapping
const paginateTestRows = (rows) => {
  console.log(`[Test Pagination] Starting dynamic pagination for ${rows.length} rows`);
  
  const pages = [];
  let currentPage = [];
  let currentPageHeight = 0;
  let isFirstPage = true;
  
  // Available heights with optimized safety buffer to prevent footer overlap
  const safetyBuffer = 12; // Slightly increased to prevent Row 24 footer overlap (was 8pt)
  const firstPageAvailableHeight = FIRST_PAGE_CONTENT_HEIGHT - TABLE_HEADER_HEIGHT - SUMMARY_HEIGHT - safetyBuffer;
  const continuationPageAvailableHeight = CONTENT_HEIGHT - TABLE_HEADER_HEIGHT - safetyBuffer;
  
  console.log(`[Test Pagination] First page available: ${firstPageAvailableHeight}pt`);
  console.log(`[Test Pagination] Continuation page available: ${continuationPageAvailableHeight}pt`);
  
  rows.forEach((row, index) => {
    const rowHeight = calculateRowHeight(row.content);
    const availableHeight = isFirstPage ? firstPageAvailableHeight : continuationPageAvailableHeight;
    
    // Check if this row fits on current page
    if (currentPageHeight + rowHeight > availableHeight && currentPage.length > 0) {
      // Start new page
      console.log(`[Test Pagination] Page ${pages.length + 1} complete: ${currentPage.length} rows, ${currentPageHeight}pt used of ${availableHeight}pt available`);
      pages.push([...currentPage]);
      currentPage = [row];
      currentPageHeight = rowHeight;
      isFirstPage = false;
    } else {
      // Add to current page
      currentPage.push(row);
      currentPageHeight += rowHeight;
    }
    
    // Update row with calculated height for rendering
    row.calculatedHeight = rowHeight;
    row.page = pages.length + 1;
  });
  
  // Add final page
  if (currentPage.length > 0) {
    console.log(`[Test Pagination] Final page ${pages.length + 1}: ${currentPage.length} rows, ${currentPageHeight}pt used`);
    pages.push(currentPage);
  }
  
  // Now update ALL rows with correct page numbers after pagination is complete
  pages.forEach((page, pageIndex) => {
    page.forEach(row => {
      row.page = pageIndex + 1;
    });
  });
  
  console.log(`[Test Pagination] Dynamic pagination complete: ${pages.length} pages`);
  return pages;
};

// Test PDF Component
const TestPaginationPDF = React.memo(({ totalRows }) => {
  console.log(`[Test PDF] Generating test PDF with ${totalRows} rows`);
  console.log(`[Test PDF] Expected first page rows: ${ROWS_PER_FIRST_PAGE}`);
  console.log(`[Test PDF] Expected continuation page rows: ${ROWS_PER_CONTINUATION_PAGE}`);
  
  // Generate test data with systematic line patterns
  const testRows = useMemo(() => {
    return Array.from({ length: totalRows }, (_, index) => {
      const rowNum = index + 1;
      const targetLines = ((rowNum - 1) % 5) + 1; // 1, 2, 3, 4, 5, 1, 2, 3, 4, 5...
      
      // Generate content for precise character counting
      let content;
      
      if (false) { // Disabled special Row 5 test - now using Lorem Ipsum for all rows
        // This section is disabled - all rows now use Lorem Ipsum
      } else {
        // Regular content generation for other rows
        const charsPerVisualLine = 73; // EXACT visual measurement
        const targetChars = targetLines * charsPerVisualLine;
        
        content = `Row ${rowNum} (${targetLines}L): `;
        const baseLength = content.length;
        const remainingChars = Math.max(0, targetChars - baseLength - 2);
        
        // Create Lorem Ipsum style text with natural word boundaries
        const loremWords = [
          "Lorem", "ipsum", "dolor", "sit", "amet", "consectetur", "adipiscing", "elit",
          "sed", "do", "eiusmod", "tempor", "incididunt", "ut", "labore", "et", "dolore",
          "magna", "aliqua", "Ut", "enim", "ad", "minim", "veniam", "quis", "nostrud",
          "exercitation", "ullamco", "laboris", "nisi", "aliquip", "ex", "ea", "commodo",
          "consequat", "Duis", "aute", "irure", "in", "reprehenderit", "voluptate",
          "velit", "esse", "cillum", "fugiat", "nulla", "pariatur", "Excepteur", "sint",
          "occaecat", "cupidatat", "non", "proident", "sunt", "culpa", "qui", "officia",
          "deserunt", "mollit", "anim", "id", "est", "laborum"
        ];
        
        const createLoremText = (targetLength) => {
          let text = "";
          let wordIndex = 0;
          
          while (text.length < targetLength) {
            const word = loremWords[wordIndex % loremWords.length];
            if (text.length + word.length + 1 <= targetLength) {
              text += (text ? " " : "") + word;
            } else {
              break;
            }
            wordIndex++;
          }
          
          return text;
        };
        
        const filler = createLoremText(Math.max(0, remainingChars));
        content += filler;
        
        // Debug log for Row 11 to see what's happening
        if (rowNum === 11) {
          console.log(`[DEBUG] Row 11: Total chars=${content.length}, Target=${targetChars}, Content="${content}"`);
        }
      }
      
      return {
        number: rowNum,
        content: content,
        targetLines: targetLines,
        actualChars: content.length,
        page: 'TBD' // Will be filled during pagination
      };
    });
  }, [totalRows]);

  // Paginate the test rows
  const paginatedRows = useMemo(() => {
    const pages = paginateTestRows(testRows);
    
    // Update each row with its page number
    pages.forEach((page, pageIndex) => {
      page.forEach(row => {
        row.page = pageIndex + 1;
      });
    });
    
    return pages;
  }, [testRows]);

  return (
    <Document>
      {paginatedRows.map((pageRows, pageIndex) => (
        <Page key={pageIndex} size="LETTER" style={styles.page}>
          {/* Header - Fixed on every page */}
          <View style={styles.header} fixed>
            <Image
              style={styles.logo}
              src={`${process.env.PUBLIC_URL}/hearsay_logo.png`}
            />
            <View style={styles.clinicInfo}>
              <Text style={styles.clinicName}>
                HearSay Speech & Hearing Centre - Milton
              </Text>
              <Text style={styles.clinicDetails}>44 Main Street E</Text>
              <Text style={styles.clinicDetails}>Milton, Ontario L9T 1N3</Text>
              <Text style={styles.clinicDetails}>Phone: 905-875-3345</Text>
              <Text style={styles.clinicDetails}>Email: milton@hearsay.ca</Text>
            </View>
          </View>
          
          <Text style={styles.title}>PDF PAGINATION TEST</Text>

          {/* Test info - Only on first page */}
          {pageIndex === 0 && (
            <View style={styles.clientInfoSection}>
              <Text style={styles.testInfo}>
                🧪 TEST PARAMETERS: {totalRows} total rows | Dynamic height calculation with {FONT_FAMILY} font | 73 chars/line | Lorem Ipsum text
              </Text>
            </View>
          )}

          {/* Table Header */}
          <View style={styles.tableHeader}>
            <Text style={[styles.numberCol, styles.tableHeaderText]}>Row #</Text>
            <Text style={[styles.contentCol, styles.tableHeaderText]}>Content</Text>
            <Text style={[styles.pageCol, styles.tableHeaderText]}>Page</Text>
          </View>

          {/* Test Rows */}
          {pageRows.length > 0 ? (
            pageRows.map((row, index) => (
              <View key={row.number} style={[styles.row, { height: row.calculatedHeight || ROW_HEIGHT }]}>
                <Text style={styles.numberCol}>{row.number}</Text>
                <Text style={styles.contentCol}>{row.content}</Text>
                <Text style={styles.pageCol}>
                  P{row.page} | {Math.ceil((row.calculatedHeight || ROW_HEIGHT) / 16)}L/{row.targetLines}L
                </Text>
              </View>
            ))
          ) : (
            <View style={[styles.row, { justifyContent: 'center' }]}>
              <Text style={{ fontSize: FONT_SIZE_NORMAL, color: "#666666" }}>
                No test rows generated
              </Text>
            </View>
          )}

          {/* Summary - Only on last page */}
          {pageIndex === paginatedRows.length - 1 && (
            <View style={styles.summarySection}>
              <Text style={styles.summaryText}>
                ✅ PAGINATION TEST COMPLETE
              </Text>
              <Text style={styles.summaryText}>
                Total: {totalRows} rows | Pages: {paginatedRows.length}
              </Text>
              <Text style={styles.summaryText}>
                Last row: {pageRows[pageRows.length - 1]?.number || 'N/A'} on Page {pageIndex + 1}
              </Text>
            </View>
          )}

          {/* Footer - Fixed on every page */}
          <View style={styles.footer} fixed>
            <Text style={styles.footerText}>
              🧪 PDF Pagination Test - Page {pageIndex + 1} of {paginatedRows.length}
            </Text>
            <Text style={styles.footerText}>
              Row Height: {ROW_HEIGHT}pt | Header: {HEADER_HEIGHT}pt | Footer: {FOOTER_HEIGHT}pt
            </Text>
          </View>
        </Page>
      ))}
    </Document>
  );
});

TestPaginationPDF.displayName = 'TestPaginationPDF';

export default function PDFPaginationTest() {
  const [totalRows, setTotalRows] = useState(90);
  const [pdfReady, setPdfReady] = useState(false);

  const handleGenerateTest = () => {
    setPdfReady(false);
    // Small delay to allow state update
    setTimeout(() => {
      setPdfReady(true);
    }, 100);
  };

  return (
    <div className="p-4">
      <div className="bg-yellow-50 border border-yellow-200 rounded-sm shadow p-6 mb-6">
        <h2 className="text-xl font-semibold mb-4 text-yellow-800">🧪 PDF Pagination Test</h2>
        <p className="text-yellow-700 mb-4">
          This tool generates a test PDF with numbered rows to validate pagination calculations and text wrapping.
          Use this to verify that rows fit correctly within page boundaries without overflowing into the footer.
        </p>
        <div className="bg-white p-3 rounded border border-yellow-200 mb-4">
          <h4 className="font-semibold text-yellow-800 mb-2">🧪 Test Variations:</h4>
          <ul className="text-sm text-yellow-700 space-y-1">
            <li><strong>Every 10th row:</strong> Extra long text to test wrapping and height impact</li>
            <li><strong>Every 5th row:</strong> Medium text to test moderate wrapping</li>
            <li><strong>Other rows:</strong> Normal short text for baseline comparison</li>
          </ul>
        </div>

        <div className="space-y-4">
          <div>
            <label className="text-sm font-medium text-yellow-800 mb-2 block">
              Number of Test Rows
            </label>
            <input
              type="number"
              min="1"
              max="200"
              value={totalRows}
              onChange={(e) => setTotalRows(parseInt(e.target.value) || 90)}
              className="w-32 p-2 border border-yellow-300 rounded bg-yellow-50 text-yellow-900"
            />
          </div>

          <div className="bg-white p-4 rounded border border-yellow-200">
            <h3 className="font-semibold text-yellow-800 mb-2">📊 Expected Pagination:</h3>
            <ul className="text-sm text-yellow-700 space-y-1">
              <li><strong>First Page:</strong> {ROWS_PER_FIRST_PAGE} rows (accounts for client info section)</li>
              <li><strong>Continuation Pages:</strong> {ROWS_PER_CONTINUATION_PAGE} rows each</li>
              <li><strong>Row Height:</strong> {ROW_HEIGHT} points</li>
              <li><strong>For {totalRows} rows:</strong> Pages calculated dynamically based on content height</li>
            </ul>
          </div>

          <button
            onClick={handleGenerateTest}
            className="px-4 py-2 bg-yellow-600 text-white rounded hover:bg-yellow-700 font-medium">
            Generate Test PDF
          </button>
        </div>
      </div>

      {pdfReady && (
        <div className="bg-white rounded-sm shadow p-6">
          <div className="flex flex-col gap-4">
            <PDFDownloadLink
              document={<TestPaginationPDF totalRows={totalRows} />}
              fileName={`pdf-pagination-test-${totalRows}-rows.pdf`}
              className="inline-block px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700">
              {({ loading }) => (loading ? "Preparing Test PDF..." : "Download Test PDF")}
            </PDFDownloadLink>

            <div className="w-full h-[600px] border rounded">
              <PDFViewer style={{ width: '100%', height: '100%' }}>
                <TestPaginationPDF totalRows={totalRows} />
              </PDFViewer>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}